import type {
  ChangeEventHandler,
  FunctionComponent,
  KeyboardEventHandler,
  MouseEventHandler,
} from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import {
  boardSwitcherSearchSharedState,
  keyboardNavSharedState,
  useBoardSwitcherMode,
} from '@trello/board-switcher';
import { intl } from '@trello/i18n';
import { Key } from '@trello/keybindings';
import { CloseIcon } from '@trello/nachos/icons/close';
import { SearchIcon } from '@trello/nachos/icons/search';
import { Textfield } from '@trello/nachos/textfield';
import { navigate } from '@trello/router/navigate';
import { useSharedStateSelector } from '@trello/shared-state';
import { getTestId, type BoardSwitcherTestIds } from '@trello/test-ids';

import { stopPropagationAndPreventDefault } from 'app/src/stopPropagationAndPreventDefault';
import { getBoardSwitcherSource } from './getBoardSwitcherSource';
import {
  getFirstNavItem,
  getNavItemElement,
  getNodeData,
} from './getNavItemTreeWalker';
import { useOpenBoardAndHideSwitcher } from './useOpenBoardAndHideSwitcher';

import * as styles from './SearchField.module.less';

export const SearchField: FunctionComponent = () => {
  const textfieldRef = useRef<HTMLInputElement>(null);

  const openBoardAndHideSwitcher = useOpenBoardAndHideSwitcher();

  const mode = useBoardSwitcherMode();

  const query = useSharedStateSelector(
    boardSwitcherSearchSharedState,
    useCallback((value) => value.query, []),
  );

  useEffect(() => {
    boardSwitcherSearchSharedState.setValue({
      searchFieldRef: textfieldRef,
    });
  }, []);

  const handleDebouncedKeydownFilter = useDebouncedCallback(() => {
    Analytics.sendUIEvent({
      action: 'keydown',
      actionSubject: 'input',
      actionSubjectId: 'searchFieldInput',
      source: getBoardSwitcherSource(),
    });
  }, 500);

  const handleInputClick = useCallback(() => {
    Analytics.sendUIEvent({
      action: 'clicked',
      actionSubject: 'input',
      actionSubjectId: 'searchFieldInput',
      source: getBoardSwitcherSource(),
    });
  }, []);

  const onSearch = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (event) => {
      boardSwitcherSearchSharedState.setValue({
        query: event.target.value,
      });
      keyboardNavSharedState.setValue({
        activeId: null,
      });

      handleDebouncedKeydownFilter();
    },
    [handleDebouncedKeydownFilter],
  );

  const onClear = useCallback<MouseEventHandler>((event) => {
    stopPropagationAndPreventDefault(event);

    Analytics.sendClickedButtonEvent({
      buttonName: 'searchFieldInputClearButton',
      source: getBoardSwitcherSource(),
    });

    boardSwitcherSearchSharedState.setValue({
      query: null,
    });
    keyboardNavSharedState.setValue({
      activeId: null,
      activeSection: null,
    });
    textfieldRef.current?.focus();
  }, []);

  const onKeyDown = useCallback<KeyboardEventHandler<HTMLInputElement>>(
    (event) => {
      const input = event.target as HTMLInputElement;

      const isAtEnd = input.selectionStart === input.value.length;

      const isEnter = event.key === Key.Enter;
      const isArrowDown = event.key === Key.ArrowDown;
      const isArrowRight = event.key === Key.ArrowRight;

      if (isEnter || (isAtEnd && (isArrowDown || isArrowRight))) {
        event.preventDefault();
        const newState = getFirstNavItem();

        if (newState) {
          keyboardNavSharedState.setValue({
            activeId: newState.activeId,
            activeSection: newState.activeSection,
          });
          if (isEnter) {
            const element = getNavItemElement(
              newState.activeSection,
              newState.activeId,
            );
            const data = getNodeData(element);

            Analytics.sendClickedLinkEvent({
              linkName: 'boardSwitcherTileLink',
              source: getBoardSwitcherSource(),
              attributes: {
                section: 'search',
              },
              containers: formatContainers({
                boardId: data?.id,
              }),
            });

            if (data?.href) {
              navigate(data.href, { trigger: true });
              openBoardAndHideSwitcher();
            }
          }
        }
      }
    },
    [openBoardAndHideSwitcher],
  );

  //unfortunately, we need to do this here instead of using the useAutoFocusRef hook because we need to not
  //autofocus when the board switcher is in panel mode
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (mode === 'panel') return;

    timeoutRef.current = setTimeout(() => {
      textfieldRef.current?.focus();
      timeoutRef.current = null;
    }, 1);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [mode, textfieldRef]);

  return (
    <div className={styles.search}>
      <Textfield
        ref={textfieldRef}
        iconBefore={<SearchIcon />}
        placeholder={intl.formatMessage({
          id: 'templates.board_switcher.search-your-boards',
          defaultMessage: 'Search your boards',
          description: 'Placeholder text on board switcher search field',
        })}
        aria-label={intl.formatMessage({
          id: 'templates.board_switcher.search-your-boards',
          defaultMessage: 'Search your boards',
          description: 'Label text on board switcher search field',
        })}
        autoFocus={mode === 'modal'}
        onClick={handleInputClick}
        onChange={onSearch}
        onKeyDown={onKeyDown}
        value={query ?? ''}
        className={styles.searchInput}
        testId={getTestId<BoardSwitcherTestIds>('board-switcher-search-field')}
      />
      {query && (
        <button
          onClick={onClear}
          className={styles.clearButton}
          aria-label={intl.formatMessage({
            id: 'templates.board_switcher.clear-boards-search',
            defaultMessage: 'Clear boards search',
            description: 'Aria label on board switcher clear search button',
          })}
        >
          <CloseIcon />
        </button>
      )}
    </div>
  );
};
